import { useState, useCallback, MouseEvent, Fragment } from "react";
import { Button, IconButton, Menu, SvgIcon, Tooltip } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import "./style.scss";
import { ReactComponent as PlayIcon } from "../../assets/play.icon.svg";
import { ReactComponent as PdfIcon } from "../../assets/file-fill.icon.svg";
import { ReactComponent as TxtIcon } from "../../assets/file.icon.svg";
import { ReactComponent as DiagramIcon } from "../../assets/diagram.icon.svg";
import { ReactComponent as SummaryIcon } from "../../assets/summary.icon.svg";
import { ReactComponent as ArrowExpandIcon } from "../../assets/arrowExpandIcon.svg";
import { ReactComponent as ArrowCollapseIcon } from "../../assets/arrowCollapseIcon.svg";
import { ReactComponent as ThumbsUpIcon } from "../../assets/thumbsUpIcon.svg";
import { ReactComponent as ThumbsDownIcon } from "../../assets/thumbsDownIcon.svg";
import { ReactComponent as LinkIcon } from "../../assets/link.icon.svg";
import TivaAvatar from "../../assets/tivaAvatar.svg";
import { getRRQuote } from "../../api/sources/endpoints-referrers";
import { FirebaseAuthContainer } from "../../common/containers/auth/firebase-auth-container.util";
import { SourceType } from "../../common/enums/source-type.enum";
import { User } from "../../common/interfaces/comps/user.interface";
import { SummaryProps } from "../../common/interfaces/chats/summary.interface";
import { Loader, DotsTyping } from "../../components/loader/loader.comp";

export const MessageResourceTooltip = ({
    children,
    metadata,
    setMetadata
}: {
    children: JSX.Element;
    metadata: any;
    setMetadata: (arg1: any) => void;
}) => {
    const isAvailable = metadata.source_type === SourceType.pdf;
    const [open, setOpen] = useState<boolean>(false);

    const handleOpen = () => {
        if (metadata.recognition_result_id && metadata.currentRange) {
            if (metadata.currentQuote) {
                setOpen(true);
            } else {
                getRRQuote(metadata.recognition_result_id, metadata.currentRange).then(response => {
                    setMetadata({ ...metadata, currentQuote: response.data });
                    setOpen(true);
                });
            }
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    return isAvailable ? (
        <Tooltip
            classes={{ popper: "message-resource-tooltip" }}
            placement="bottom-start"
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            title={
                <div className="mr-tooltip-container">
                    <SvgIcon className="mr-tooltip-icon" component={LinkIcon} inheritViewBox />
                    <div className="mr-tooltip-content">
                        <span className="mr-tooltip-content-filename">{metadata.source}</span>
                        <div className="mr-tooltip-content-quote">
                            <span className="mr-tooltip-content-quote__text">
                                {metadata.currentQuote}
                            </span>
                            <span className="mr-tooltip-content-quote__page">
                                (Page {metadata.currentRange})
                            </span>
                        </div>
                    </div>
                </div>
            }
        >
            {children}
        </Tooltip>
    ) : (
        <>{children}</>
    );
};

export const MediaButton = ({
    source,
    handleLink,
    className = ""
}: {
    source: any;
    handleLink: (arg1: any) => void;
    className?: string;
}) => {
    const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
    const menuOpen = Boolean(menuAnchor);

    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        if (hasPages(source)) {
            setMenuAnchor(event.currentTarget);
        } else {
            handleLink(source);
        }
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const hasPages = (source: any) =>
        source.source_type === SourceType.pdf &&
        !!source.pageList?.filter((page: any) => page.number > 0).length;

    const isMedia = (type: string) => {
        return type === SourceType.video || type === SourceType.mp4 || type === SourceType.mp3;
    };

    const isVideo = (type: string) => {
        return type === SourceType.video || type === SourceType.mp4;
    };

    const sourceName = (item: any) => {
        return (
            item.source ??
            (isVideo(item.source_type)
                ? "Video link"
                : item.source_type === SourceType.mp3
                  ? "Audio link"
                  : item.source_type === SourceType.pdf
                    ? "PDF link"
                    : "Document link")
        );
    };

    return (
        <Fragment>
            <Button
                className={`media-button ${className}`}
                variant="text"
                startIcon={
                    <SvgIcon
                        className="media-button__icon--start"
                        component={
                            isMedia(source.source_type)
                                ? PlayIcon
                                : source.source_type === SourceType.pdf
                                  ? PdfIcon
                                  : TxtIcon
                        }
                        inheritViewBox
                    />
                }
                endIcon={
                    hasPages(source) ? (
                        <SvgIcon
                            className="media-button__icon--end"
                            component={menuOpen ? KeyboardArrowUpIcon : KeyboardArrowDownIcon}
                            inheritViewBox
                        />
                    ) : null
                }
                onClick={handleMenuClick}
            >
                <span className="media-button__label">{sourceName(source)}</span>
            </Button>
            <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                    "aria-labelledby": "basic-button"
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "media-button-menu"
                    }
                }}
                transformOrigin={{ horizontal: "left", vertical: "top" }}
                anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
            >
                <div className="media-button-menu-scroll">
                    {source.pageList.map((page: any, index: number) => (
                        <div key={crypto.randomUUID()} className="media-button-menu-item">
                            {index > 0 && <div className="media-button-menu-item-separator"></div>}
                            <span
                                className="media-button-menu-item-title"
                                onClick={() => handleLink({ ...source, currentPage: page.number })}
                            >
                                page {page.number}
                            </span>
                            <div
                                className="media-button-menu-item-description"
                                dangerouslySetInnerHTML={{ __html: page.quote }}
                            />
                        </div>
                    ))}
                </div>
            </Menu>
        </Fragment>
    );
};

export enum ChatBubbleType {
    question,
    answer,
    summary,
    typing,
    error,
    tivaData
}

export enum Feedback {
    like = "1",
    dislike = "-1",
    noReview = "0"
}

export const ChatBubble = ({
    id,
    message,
    isLoading,
    type,
    data,
    metadata,
    handleLink,
    handleTivaDataResult,
    handleSummarize,
    handleReview,
    isActive = false,
    review,
    showReview = false,
    showSources = false,
    showSummary = false,
    hideAvatars = false,
    isExpanded = false,
    setExpanded
}: {
    id?: number;
    message: string;
    isLoading: boolean;
    type: ChatBubbleType;
    data?: any;
    metadata?: any;
    handleLink?: (arg1: any) => void;
    handleTivaDataResult?: (arg1: any) => void;
    handleSummarize?: (summaryData: SummaryProps) => void;
    handleReview?: (arg1: number, arg2: Feedback) => void;
    isActive: boolean;
    review?: Feedback;
    showReview?: boolean;
    showSources?: boolean;
    showSummary?: boolean;
    hideAvatars?: boolean;
    isExpanded?: boolean;
    setExpanded?: (arg1: boolean) => void;
}) => {
    const parser = new DOMParser();
    const { user }: { user: User | null } = FirebaseAuthContainer.useContainer();
    const [messageElement, setMessageElement] = useState<HTMLElement>();
    const [isQuestion] = useState<boolean>(type === ChatBubbleType.question);
    const [isError] = useState<boolean>(type === ChatBubbleType.error);
    const [classes] = useState<string>(
        `chat-bubble-content chat-bubble-content--${isQuestion ? "question" : "answer"} ${isError ? "chat-bubble-content--error" : ""} ${isActive ? "chat-bubble-content--active" : ""}`
    );
    const [sources] = useState<any[]>(() =>
        (metadata
            ? metadata.filter(
                  (item: any) =>
                      item.id_course !== undefined &&
                      item.id_course !== null &&
                      item.id_course !== "" &&
                      item.lo_item_id !== undefined &&
                      item.lo_item_id !== null &&
                      item.lo_item_id !== ""
              )
            : []
        )
            .map((item: any) => ({
                ...item,
                quote: item.quote
                    .replace(/<img[^>]+src="(?!(http|data))[^"]+"[^>]+(\/>|><\/img>)/g, "")
                    .replace(/<[^/>]+><\/[^>]+>/g, "")
                    .replace(/<section[^>]+><\/section>/g, "")
            }))
            .reduce((result: any[], item: any) => {
                const doc = parser.parseFromString(item.quote, "text/html");
                return doc.body.hasChildNodes() && !!doc.querySelectorAll("section").length
                    ? [
                          ...result,
                          ...[...doc.body.childNodes]
                              .map(node => node as HTMLElement)
                              .filter(node => node.getAttribute("doc_loc"))
                              .map(node => ({
                                  ...item,
                                  quote:
                                      node.nodeName === "SECTION" ? node.innerHTML : node.outerHTML,
                                  page_number:
                                      Number(
                                          node
                                              .getAttribute("doc_loc")
                                              ?.replace(/0*([1-9][0-9]*)[^]*/g, "$1")
                                      ) || 0
                              }))
                              .reduce(
                                  (result: any[], item: any) =>
                                      result.find(
                                          resultItem => resultItem.page_number === item.page_number
                                      )
                                          ? result.map(resultItem => ({
                                                ...resultItem,
                                                quote: `${resultItem.quote}${item.quote}`
                                            }))
                                          : [...result, item],
                                  []
                              )
                      ]
                    : [...result, item];
            }, [])
            .reduce(
                (result: any[], item: any) =>
                    result.find(
                        resultItem =>
                            resultItem.id_course === item.id_course &&
                            resultItem.lo_item_id === item.lo_item_id
                    )
                        ? result.map(resultItem =>
                              resultItem.id_course === item.id_course &&
                              resultItem.lo_item_id === item.lo_item_id
                                  ? {
                                        ...resultItem,
                                        pageList: [
                                            ...resultItem.pageList,
                                            {
                                                endPosition: item.end_position,
                                                startPosition: item.start_position,
                                                number: item.page_number,
                                                relevance: item.relevance,
                                                score: item.score,
                                                quote: item.quote
                                            }
                                        ]
                                    }
                                  : resultItem
                          )
                        : [
                              ...result,
                              {
                                  ...item,
                                  pageList: [
                                      {
                                          endPosition: item.end_position,
                                          startPosition: item.start_position,
                                          number: item.page_number,
                                          relevance: item.relevance,
                                          score: item.score,
                                          quote: item.quote
                                      }
                                  ],
                                  end_position: undefined,
                                  start_position: undefined,
                                  page_number: undefined,
                                  relevance: undefined,
                                  score: undefined,
                                  quote: undefined
                              }
                          ],
                []
            )
            .map((item: any) => ({
                ...item,
                pageList: item.pageList.sort((a: any, b: any) => b.relevance - a.relevance)
            }))
    );

    const messageRef = useCallback((node: HTMLElement) => {
        setMessageElement(node);
    }, []);

    const isClamped = () => {
        return (
            type === ChatBubbleType.question &&
            id &&
            messageElement &&
            (messageElement.scrollHeight > messageElement.clientHeight ||
                messageElement.clientHeight > 80)
        );
    };

    const handleExpandCollapseClick = () => {
        if (setExpanded) {
            setExpanded(!isExpanded);
        }
    };

    const handleLinkClick = (lo_metadata: any) => {
        if (handleLink) {
            handleLink(lo_metadata);
        }
    };

    const handleSummaryClick = (lo_metadata: any) => {
        if (handleSummarize) {
            handleSummarize({
                courseId: lo_metadata.id_course,
                loItemId: lo_metadata.lo_item_id
            });
        }
    };

    const handleReviewClick = (feedback: Feedback) => {
        if (id && handleReview) {
            handleReview(id, feedback);
        }
    };

    const handleTivaDataResultClick = () => {
        if (handleTivaDataResult) {
            handleTivaDataResult(data);
        }
    };

    // const updateSources = (source: any) => {
    //     setSources(items =>
    //         items.map(item =>
    //             item.recognition_result_id === source.recognition_result_id &&
    //             item.currentRange === source.currentRange
    //                 ? { ...source }
    //                 : item
    //         )
    //     );
    // };

    return (
        <div
            className="chat-bubble"
            style={{ justifyContent: isQuestion ? "flex-end" : "flex-start" }}
        >
            {isQuestion && isLoading && (
                <Loader className="chat-bubble-loader" size={24} borderWidth={4} />
            )}
            {!hideAvatars && type !== ChatBubbleType.question && (
                <div className="chat-bubble-avatar chat-bubble-avatar--answer">
                    <img src={TivaAvatar} alt="" />
                </div>
            )}
            <div className={classes}>
                {data && type !== ChatBubbleType.error && (
                    <span className="chat-bubble-content__title">TIVA Data:</span>
                )}
                <span
                    ref={messageRef}
                    className={`chat-bubble-content__text${isExpanded ? " chat-bubble-content__text--expanded" : ""}`}
                >
                    <Markdown remarkPlugins={[remarkGfm]}>{message}</Markdown>
                </span>
                {!isQuestion && isLoading && <DotsTyping />}
                {showSources && sources.length > 0 && (
                    <div className="chat-bubble-metadata">
                        <div className="chat-bubble-metadata__title">Relevant resources:</div>
                        <div className="chat-bubble-metadata__resources">
                            {sources.map((item: any) => (
                                <div key={crypto.randomUUID()} className="chat-bubble-rosource">
                                    <MediaButton
                                        className="chat-bubble-rosource--link"
                                        source={item}
                                        handleLink={handleLinkClick}
                                    />
                                    {showSummary && (
                                        <Button
                                            className="chat-bubble-rosource--summary transparent-button--icon-first"
                                            variant="text"
                                            startIcon={
                                                <SvgIcon
                                                    className="transparent-button--icon-first__icon"
                                                    component={SummaryIcon}
                                                    inheritViewBox
                                                />
                                            }
                                            onClick={() => handleSummaryClick(item)}
                                        >
                                            <span className="transparent-button--icon-first__text">
                                                AI summary
                                            </span>
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {data?.payload && type !== ChatBubbleType.error && (
                    <div className="chat-bubble-data">
                        <Button
                            className="chat-bubble-data__button"
                            variant="text"
                            startIcon={
                                <SvgIcon
                                    className="chat-bubble-data__button-icon"
                                    component={DiagramIcon}
                                    inheritViewBox
                                />
                            }
                            onClick={handleTivaDataResultClick}
                        >
                            <span className="chat-bubble-data__button-text">
                                Open TIVA Data Results
                            </span>
                        </Button>
                    </div>
                )}
                {showReview &&
                    (type === ChatBubbleType.answer || type === ChatBubbleType.tivaData) && (
                        <div className="chat-bubble-actions">
                            <IconButton
                                className={`chat-bubble-actions__button-icon${review === Feedback.like ? " chat-bubble-actions__button-icon--fill" : ""}`}
                                onClick={() =>
                                    handleReviewClick(
                                        review === Feedback.like ? Feedback.noReview : Feedback.like
                                    )
                                }
                            >
                                <SvgIcon component={ThumbsUpIcon} inheritViewBox />
                            </IconButton>
                            <IconButton
                                className={`chat-bubble-actions__button-icon${review === Feedback.dislike ? " chat-bubble-actions__button-icon--fill" : ""}`}
                                onClick={() =>
                                    handleReviewClick(
                                        review === Feedback.dislike
                                            ? Feedback.noReview
                                            : Feedback.dislike
                                    )
                                }
                            >
                                <SvgIcon component={ThumbsDownIcon} inheritViewBox />
                            </IconButton>
                        </div>
                    )}
                {isClamped() && (
                    <div
                        className={`chat-bubble-collapse${isExpanded ? " chat-bubble-collapse--hidden" : ""}`}
                    >
                        <div className="chat-bubble-collapse__gradient"></div>
                        <div className="chat-bubble-collapse__button">
                            <IconButton onClick={() => handleExpandCollapseClick()}>
                                <SvgIcon
                                    component={isExpanded ? ArrowCollapseIcon : ArrowExpandIcon}
                                    inheritViewBox
                                />
                            </IconButton>
                        </div>
                    </div>
                )}
            </div>
            {!hideAvatars && user && type === ChatBubbleType.question && (
                <div className="chat-bubble-avatar chat-bubble-avatar--question">
                    {user.avatar ? (
                        <img src={user.avatar} alt="" />
                    ) : (
                        <div>
                            {user.firstName?.toUpperCase().at(0) ?? user.email.toUpperCase().at(0)}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
